import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useWallet } from '@solana/wallet-adapter-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import {
  Search,
  Filter,
  TrendingUp,
  Clock,
  Users,
  Brain,
  Calendar,
  DollarSign,
  ChevronRight,
  Loader,
  Activity,
  CheckCircle,
  RefreshCw,
  Star,
  AlertTriangle,
  X,
  Share2,
  Trash2,
  Trophy,
  Gamepad2,
  Sparkles,
  Coins
} from 'lucide-react'
import { useAgiAuthStore, agiApi } from '../store/agiAuthStore'

// Interface for real market data from backend
interface Market {
  id: number
  question: string
  category: string
  creator_id?: number
  creator_name?: string
  creator_address?: string
  creator_email?: string
  status: string
  total_yes_bets: number
  total_no_bets: number
  total_volume: number
  betting_ends_at: string
  resolution_time: string
  created_at: string
  outcome?: number
  resolved_at?: string
  // Fantasy contest fields (when category is 'fantasy')
  isFantasyContest?: boolean
  contest_type?: string
  entry_fee?: number
  max_entries?: number
  current_entries?: number
  prize_pool?: number
  roster_lock_time?: string
  // AGI Market fields (when category is 'agi_manual')
  isAgiMarket?: boolean
  resolution_rules?: string
  agi_odds_reasoning?: string
}

const categories = [
  'All Categories',
  'crypto',
  'stocks',
  'sports',
  'fantasy',
  'business',
  'real_assets',
  'world_events',
  'politics',
  'entertainment',
  'social',
  'weather',
  'finance',
  'other'
]

const sortOptions = [
  { value: 'volume', label: 'Highest Volume' },
  { value: 'recent', label: 'Most Recent' },
  { value: 'closing', label: 'Closing Soon' }
]

export default function MarketsPage() {
  const { publicKey } = useWallet()
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All Categories')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [sortBy, setSortBy] = useState('volume')
  const [markets, setMarkets] = useState<Market[]>([])
  const [loading, setLoading] = useState(true)
  const [favorites, setFavorites] = useState<number[]>([])
  const [showLiveOnly, setShowLiveOnly] = useState(false)
  
  // AGI Auth - for accessing AGI Markets
  const { agiUser, isAgiAuthenticated, agiToken } = useAgiAuthStore()
  const isAgiUser = isAgiAuthenticated && agiUser?.isAgi
  
  // Fetch real markets from the database (re-fetch when URL changes)
  useEffect(() => {
    fetchMarkets()
  }, [location.search])
  
  // Load favorites when wallet connects
  useEffect(() => {
    if (publicKey) {
      const stored = localStorage.getItem(`favorites_${publicKey.toString()}`)
      if (stored) {
        setFavorites(JSON.parse(stored))
      }
    }
  }, [publicKey])
  
  // Watch for URL changes and update filters
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const categoryParam = params.get('category')
    const liveParam = params.get('live')
    const newsBotParam = params.get('newsbot')
    
    if (categoryParam) {
      setSelectedCategory(categoryParam)
    } else {
      setSelectedCategory('All Categories')
    }
    
    if (liveParam === 'true') {
      setShowLiveOnly(true)
    } else if (newsBotParam === 'true') {
      // News Bot filter active
      setShowLiveOnly(false)
    } else {
      setShowLiveOnly(false)
    }
  }, [location.search])
  
  // Toggle favorite
  const toggleFavorite = (e: React.MouseEvent, marketId: number) => {
    e.preventDefault() // Prevent navigation to market detail
    e.stopPropagation()
    
    if (!publicKey) {
      toast.error('Connect wallet to save favorites')
      return
    }
    
    const newFavorites = favorites.includes(marketId)
      ? favorites.filter(id => id !== marketId)
      : [...favorites, marketId]
    
    setFavorites(newFavorites)
    localStorage.setItem(`favorites_${publicKey.toString()}`, JSON.stringify(newFavorites))
    toast.success(favorites.includes(marketId) ? 'Removed from favorites' : 'Added to favorites!')
  }
  
  // Delete market (for demo cleanup)
  const handleDelete = async (e: React.MouseEvent, marketId: number) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!confirm('Delete this market? (Demo cleanup only)')) {
      return
    }
    
    try {
      const response = await axios.delete(`/api/markets/${marketId}`)
      if (response.data.success) {
        toast.success('Market deleted')
        // Remove from local state
        setMarkets(prev => prev.filter(m => m.id !== marketId))
      }
    } catch (error: any) {
      console.error('Delete error:', error)
      toast.error(error.response?.data?.error || 'Failed to delete market')
    }
  }
  
  // Delete fantasy contest (for demo cleanup)
  const handleDeleteFantasy = async (e: React.MouseEvent, contestId: number) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!confirm('Delete this fantasy contest? All entries will be removed. (Demo cleanup only)')) {
      return
    }
    
    try {
      const response = await axios.delete(`/api/fantasy/contests/${contestId}`)
      if (response.data.success) {
        toast.success('Fantasy contest deleted')
        // Remove from local state
        setMarkets(prev => prev.filter(m => !(m.isFantasyContest && m.id === contestId)))
      }
    } catch (error: any) {
      console.error('Delete fantasy error:', error)
      toast.error(error.response?.data?.error || 'Failed to delete contest')
    }
  }
  
  // Share market
  const handleShare = async (e: React.MouseEvent, market: Market) => {
    e.preventDefault()
    e.stopPropagation()
    
    const shareData = {
      title: `CPX Market: ${market.question}`,
      text: `Check out this prediction market: ${market.question}`,
      url: `${window.location.origin}/markets/${market.id}`
    }
    
    try {
      // Try Web Share API first (works on mobile and some desktop browsers)
      if (navigator.share) {
        await navigator.share(shareData)
        toast.success('Shared successfully!')
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(shareData.url)
        toast.success('Link copied to clipboard!')
      }
    } catch (error: any) {
      // User cancelled or error occurred
      if (error.name !== 'AbortError') {
        // Fallback: Copy to clipboard
        try {
          await navigator.clipboard.writeText(shareData.url)
          toast.success('Link copied to clipboard!')
        } catch {
          toast.error('Failed to share')
        }
      }
    }
  }

  const fetchMarkets = async () => {
    try {
      setLoading(true)
      
      // Build query params from URL
      const params = new URLSearchParams(location.search)
      const apiParams = new URLSearchParams()
      apiParams.set('limit', '200')
      
      // Pass filters to backend API
      const categoryFilter = params.get('category')
      if (categoryFilter) {
        apiParams.set('category', categoryFilter)
      }
      if (params.get('live') === 'true') {
        apiParams.set('live', 'true')
      }
      if (params.get('newsbot') === 'true') {
        apiParams.set('newsbot', 'true')
        console.log('🤖 Fetching News Bot markets only...')
      }
      
      // If fantasy category, fetch fantasy contests instead
      if (categoryFilter === 'fantasy') {
        console.log('🏈 Fetching Fantasy contests...')
        try {
          const fantasyResponse = await axios.get('/api/fantasy/contests')
          const fantasyContests = (fantasyResponse.data.contests || []).map((c: any) => ({
            id: c.id,
            question: c.title,
            category: 'fantasy',
            creator_name: c.creator_name,
            status: c.status === 'open' ? 'active' : c.status,
            total_yes_bets: c.current_entries || c.entryCount || 0,
            total_no_bets: 0,
            total_volume: c.prize_pool || ((c.current_entries || 0) * (c.entry_fee || 0)),
            betting_ends_at: c.roster_lock_time,
            resolution_time: c.resolution_time,
            created_at: c.created_at,
            // Fantasy-specific fields
            isFantasyContest: true,
            contest_type: c.contest_type,
            entry_fee: c.entry_fee,
            max_entries: c.max_entries,
            current_entries: c.current_entries || c.entryCount || 0,
            prize_pool: c.prize_pool,
            roster_lock_time: c.roster_lock_time
          }))
          setMarkets(fantasyContests)
          console.log('✅ Loaded', fantasyContests.length, 'fantasy contests')
        } catch (error) {
          console.error('Failed to fetch fantasy contests:', error)
          setMarkets([])
        }
      } else if (categoryFilter === 'agi_manual') {
        // AGI Markets - only for @agi.live users
        console.log('✨ Fetching AGI Events...')
        if (!isAgiUser) {
          console.log('❌ Not an AGI user, cannot view AGI markets')
          setMarkets([])
        } else {
          try {
            agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
            const agiResponse = await agiApi.get('/api/agi/markets')
            const agiMarkets = (agiResponse.data.markets || []).map((m: any) => ({
              ...m,
              isAgiMarket: true,
              category: 'agi_manual'
            }))
            setMarkets(agiMarkets)
            console.log('✅ Loaded', agiMarkets.length, 'AGI markets')
          } catch (error) {
            console.error('Failed to fetch AGI markets:', error)
            setMarkets([])
          }
        }
      } else {
      const response = await axios.get(`/api/markets?${apiParams.toString()}`)
      // Filter out AGI markets from regular view (only show to @agi.live users via category filter)
      const allMarkets = response.data.markets || response.data || []
      const filteredMarkets = allMarkets.filter((m: any) => m.category !== 'agi_manual' || isAgiUser)
      setMarkets(filteredMarkets)
      console.log('✅ Loaded', filteredMarkets.length, 'markets from API')
      }
    } catch (error) {
      console.error('Failed to fetch markets:', error)
      toast.error('Failed to load markets')
      setMarkets([])
    } finally {
      setLoading(false)
    }
  }
  
  // Helper: Check if market is LIVE (happening soon or now)
  const isLiveMarket = (market: Market) => {
    // Has a live tracker widget
    return !!(market as any).widget_config
  }
  
  // Helper: Check if market is LIVE (event happening RIGHT NOW or very soon)
  const isFlashMarket = (market: Market) => {
    // Must have live tracker widget
    if (!(market as any).widget_config) return false
    
    // Market must be active (not resolved/disputed)
    if (market.status !== 'active') return false
    
    // Simple check: Is the event scheduled for TODAY?
    // This avoids showing old October events as "LIVE NOW"
    if (!market.resolution_time) return false
    
    const today = new Date()
    const eventDate = new Date(market.resolution_time)
    
    // Check if same calendar day
    const isToday = today.getFullYear() === eventDate.getFullYear() &&
                    today.getMonth() === eventDate.getMonth() &&
                    today.getDate() === eventDate.getDate()
    
    // LIVE NOW = Has widget + active + event is TODAY
    return isToday
  }
  
  // Calculate stats from real data
  const activeMarkets = markets.filter(m => m.status === 'active' || m.status === 'pending_validation')
  const totalVolume = markets.reduce((sum, m) => sum + (m.total_volume || 0), 0)
  const resolvedMarkets = markets.filter(m => m.status === 'resolved')
  const disputedMarkets = markets.filter(m => m.status === 'disputed')
  const liveMarkets = markets.filter(m => isLiveMarket(m)) // Has widget (scheduled or happening)
  const flashMarkets = markets.filter(m => isFlashMarket(m)) // Actually happening RIGHT NOW
  
  // Filter markets based on search and filters
  const filteredMarkets = markets.filter(market => {
    // Enhanced search: question, category, or creator name
    const searchLower = searchQuery.toLowerCase()
    const matchesSearch = searchQuery === '' ||
      market.question.toLowerCase().includes(searchLower) ||
      market.category.toLowerCase().includes(searchLower) ||
      market.creator_name?.toLowerCase().includes(searchLower) ||
      false
    
    // Category filter - handle both 'All Categories' and specific categories
    const matchesCategory = selectedCategory === 'All Categories' || 
      selectedCategory === '' ||
      market.category?.toLowerCase() === selectedCategory.toLowerCase()
    
    const matchesStatus = selectedStatus === 'all' || 
      (selectedStatus === 'active' && (market.status === 'active' || market.status === 'pending_validation')) ||
      (selectedStatus === 'resolved' && market.status === 'resolved') ||
      (selectedStatus === 'disputed' && market.status === 'disputed') ||
      (selectedStatus === 'resolving' && market.status === 'resolving')
    
    // Live filter: Only show FLASH markets (happening NOW) when live filter is active
    const matchesLive = !showLiveOnly || isFlashMarket(market)
    
    return matchesSearch && matchesCategory && matchesStatus && matchesLive
  })
  
  // Sort markets - FLASH markets always at top!
  const sortedMarkets = [...filteredMarkets].sort((a, b) => {
    // Priority 1: Flash markets ALWAYS first
    const aIsFlash = isFlashMarket(a)
    const bIsFlash = isFlashMarket(b)
    
    if (aIsFlash && !bIsFlash) return -1
    if (!aIsFlash && bIsFlash) return 1
    
    // Priority 2: Apply user's selected sort
    switch(sortBy) {
      case 'volume':
        return (b.total_volume || 0) - (a.total_volume || 0)
      case 'recent':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      case 'closing':
        const aClose = new Date(a.betting_ends_at || '9999').getTime()
        const bClose = new Date(b.betting_ends_at || '9999').getTime()
        return aClose - bClose
      default:
        return 0
    }
  })

  const getStatusBadge = (status: string, outcome?: number, market?: Market) => {
    // Check if this is a FLASH market (happening RIGHT NOW)
    const isFlash = market && isFlashMarket(market)
    
    if (status === 'resolved') {
      return (
        <div className="flex items-center space-x-2">
          <div className="px-2 py-1 rounded text-xs font-semibold bg-gray-800 text-gray-400">Resolved</div>
          {outcome !== undefined && (
            <div className={`px-2 py-1 rounded text-xs font-semibold ${outcome === 1 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
              {outcome === 1 ? 'YES' : 'NO'}
            </div>
          )}
        </div>
      )
    }
    if (status === 'disputed') {
      return (
        <div className="px-2 py-1 rounded text-xs font-semibold bg-red-500/20 text-red-400 flex items-center">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Disputed
        </div>
      )
    }
    if (status === 'pending_validation') {
      return <div className="px-2 py-1 rounded text-xs font-semibold bg-amber-500/20 text-amber-400">Pending</div>
    }
    if (status === 'resolving') {
      return (
        <div className="px-2 py-1 rounded text-xs font-semibold bg-purple-500/20 text-purple-400 flex items-center">
          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
          Resolving
        </div>
      )
    }
    
    // FLASH MARKET - Happening RIGHT NOW with pulsing indicator
    if (isFlash) {
      return (
        <div className="relative flex items-center space-x-2">
          <div className="px-2 py-1 rounded text-xs font-bold bg-red-500/20 text-red-400 flex items-center border border-red-500/40">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-1.5 animate-pulse"></div>
            LIVE NOW
          </div>
        </div>
      )
    }
    
    return <div className="px-2 py-1 rounded text-xs font-semibold bg-cyan-500/20 text-cyan-400">Active</div>
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No end date'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getDaysUntil = (dateString: string) => {
    if (!dateString) return 'No end date'
    const target = new Date(dateString)
    const now = new Date()
    const diffTime = target.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return 'Closed'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return '1 day'
    return `${diffDays} days`
  }

  const calculateOdds = (yesAmount: number, noAmount: number) => {
    const total = yesAmount + noAmount
    if (total === 0) return { yes: 50, no: 50 }
    return {
      yes: Math.round((yesAmount / total) * 100),
      no: Math.round((noAmount / total) * 100)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-cpx-light-blue mx-auto mb-4" />
          <p className="text-gray-400">Loading markets...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-1">Markets</h1>
          <p className="text-gray-300 text-sm font-medium">
            {activeMarkets.length} active • {totalVolume.toLocaleString()} volume
          </p>
        </div>

        {/* Stats Grid - Compact */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          <div className="bg-[#001233] rounded-lg p-3 border border-[#052393]/40">
            <div className="text-xl font-bold text-white">{activeMarkets.length}</div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Active</div>
          </div>
          <div className="bg-[#001233] rounded-lg p-3 border border-red-500/30 relative overflow-hidden">
            {flashMarkets.length > 0 && (
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-orange-500/5 animate-pulse"></div>
            )}
            <div className="relative z-10">
              <div className="text-xl font-bold text-red-400 flex items-center">
                {flashMarkets.length > 0 && <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>}
                {flashMarkets.length}
              </div>
              <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Flash</div>
            </div>
          </div>
          <div className="bg-[#001233] rounded-lg p-3 border border-[#052393]/40">
            <div className="text-xl font-bold text-white">{resolvedMarkets.length}</div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Resolved</div>
          </div>
          <div className="bg-[#001233] rounded-lg p-3 border border-red-500/20">
            <div className="text-xl font-bold text-red-400">{disputedMarkets.length}</div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Disputed</div>
          </div>
          <div className="bg-[#001233] rounded-lg p-3 border border-[#052393]/40">
            <div className="text-xl font-bold text-white">{(totalVolume / 1000).toFixed(1)}k</div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Volume</div>
          </div>
        </div>

        {/* Filters - Compact Row */}
        <div className="flex items-center space-x-3 mb-6">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by question, category, or creator..."
              className="w-full bg-[#001233] border border-[#052393]/40 rounded-lg pl-10 pr-10 py-2.5 text-sm text-white placeholder-gray-500 focus:border-[#2AAAEA] focus:ring-1 focus:ring-[#2AAAEA]/30 transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedStatus('active')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-150 ${
                selectedStatus === 'active' 
                  ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393] text-white' 
                  : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setSelectedStatus('resolved')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-150 ${
                selectedStatus === 'resolved' 
                  ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393] text-white' 
                  : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
              }`}
            >
              Resolved
            </button>
            <button
              onClick={() => setSelectedStatus('disputed')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-150 flex items-center space-x-1 ${
                selectedStatus === 'disputed' 
                  ? 'bg-gradient-to-r from-red-500 to-red-700 text-white' 
                  : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
              }`}
            >
              <AlertTriangle className="w-3 h-3" />
              <span>Disputed</span>
            </button>
            <button
              onClick={() => setSelectedStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-150 ${
                selectedStatus === 'all' 
                  ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393] text-white' 
                  : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
              }`}
            >
              All
            </button>
            
            {/* Divider */}
            <div className="w-px h-8 bg-[#052393]/40"></div>
            
            {/* Fantasy Toggle */}
            <Link
              to={selectedCategory === 'fantasy' ? '/markets' : '/markets?category=fantasy'}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-150 flex items-center space-x-2 ${
                selectedCategory === 'fantasy'
                  ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg shadow-emerald-500/20'
                  : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30'
              }`}
            >
              <Gamepad2 className="w-4 h-4" />
              <span>🏈 Fantasy</span>
            </Link>
            
            {/* AGI Markets Toggle - Only show for AGI users */}
            {isAgiUser && (
              <Link
                to={selectedCategory === 'agi_manual' ? '/markets' : '/markets?category=agi_manual'}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-150 flex items-center space-x-2 ${
                  selectedCategory === 'agi_manual'
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg shadow-yellow-500/20'
                    : 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/30'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                <span>✨ AGI</span>
              </Link>
            )}
          </div>
        </div>

        {/* Markets Grid - Myriad Style */}
        {sortedMarkets.length === 0 ? (
          <div className="text-center py-16">
            {selectedCategory === 'fantasy' ? (
              <>
                <Trophy className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-300 mb-2">No fantasy contests yet</h3>
                <p className="text-gray-500 mb-6">Be the first to create a fantasy football contest!</p>
                <Link
                  to="/fantasy/create"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-500 transition-colors"
                >
                  <Gamepad2 className="w-5 h-5" />
                  Create Fantasy Contest
                </Link>
              </>
            ) : selectedCategory === 'agi_manual' ? (
              <>
                <Sparkles className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-300 mb-2">No AGI markets yet</h3>
                <p className="text-gray-500 mb-6">Create the first internal AGI prediction market!</p>
                <Link
                  to="/create/agi-event"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-lg hover:from-yellow-400 hover:to-orange-400 transition-colors"
                >
                  <Sparkles className="w-5 h-5" />
                  Create AGI Event
                </Link>
              </>
            ) : (
              <>
            <Brain className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No markets found</h3>
            <p className="text-gray-500">
              {markets.length === 0 
                ? 'Be the first to create a market!' 
                : 'Try adjusting your filters'}
            </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedMarkets.map((market, index) => {
              const odds = calculateOdds(market.total_yes_bets || 0, market.total_no_bets || 0)
              const isFlash = isFlashMarket(market)
              const isFantasy = market.isFantasyContest
              
              // Fantasy contest card
              if (isFantasy) {
                const contestTypeLabel = market.contest_type === 'dfs_weekly' ? 'DFS Weekly' :
                  market.contest_type === 'single_game' ? 'Single Game' : 'One-and-Done'
                const contestTypeColor = market.contest_type === 'dfs_weekly' ? 'bg-blue-500/20 text-blue-400' :
                  market.contest_type === 'single_game' ? 'bg-purple-500/20 text-purple-400' : 'bg-orange-500/20 text-orange-400'
                const lockTime = new Date(market.roster_lock_time || market.betting_ends_at)
                const isLocked = new Date() >= lockTime
                
                return (
                  <motion.div
                    key={`fantasy-${market.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="h-full"
                  >
                    <Link
                      to={`/fantasy/${market.id}`}
                      className="block h-full bg-[#001233] rounded-xl transition-all duration-200 overflow-hidden group relative border border-emerald-500/30 hover:border-emerald-400/60 hover:shadow-lg hover:shadow-emerald-500/10"
                    >
                      {/* Fantasy gradient accent */}
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent pointer-events-none"></div>
                      
                      {/* Card Header */}
                      <div className="p-5 relative z-10 flex flex-col h-full">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">🏈</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${contestTypeColor}`}>
                              {contestTypeLabel}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isLocked ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'
                            }`}>
                              {isLocked ? 'Locked' : 'Open'}
                            </span>
                            <button
                              onClick={(e) => handleDeleteFantasy(e, market.id)}
                              className="p-1.5 rounded-lg transition-all text-gray-500 hover:text-red-500 hover:bg-red-500/10"
                              title="Delete contest (Demo only)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        
                        <h3 className="text-base font-bold text-white group-hover:text-emerald-300 transition-colors line-clamp-2 mb-3 min-h-[48px]">
                          {market.question}
                        </h3>
                        
                        {/* Fantasy Stats */}
                        <div className="grid grid-cols-2 gap-3 mb-3 mt-auto">
                          <div className="flex items-center gap-2 text-sm">
                            <DollarSign className="w-4 h-4 text-emerald-400" />
                            <span className="text-gray-300">
                              {market.entry_fee ? `$${market.entry_fee}` : 'Free'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="w-4 h-4 text-blue-400" />
                            <span className="text-gray-300">
                              {market.current_entries || 0} / {market.max_entries || '∞'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Trophy className="w-4 h-4 text-yellow-400" />
                            <span className="text-gray-300">
                              ${market.prize_pool || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-purple-400" />
                            <span className="text-gray-300">
                              {getDaysUntil(market.roster_lock_time || market.betting_ends_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hover Footer */}
                      <div className="bg-emerald-500/10 px-5 py-2.5 flex items-center justify-between border-t border-emerald-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <span className="text-xs text-white font-bold uppercase tracking-wider">
                          {isLocked ? 'View Contest' : 'Enter Contest'}
                        </span>
                        <ChevronRight className="w-4 h-4 text-emerald-400" />
                      </div>
                    </Link>
                  </motion.div>
                )
              }
              
              // AGI Market card
              const isAgi = market.isAgiMarket || market.category === 'agi_manual'
              if (isAgi) {
                return (
                  <motion.div
                    key={`agi-${market.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="h-full"
                  >
                    <Link
                      to={`/markets/${market.id}`}
                      className="block h-full bg-[#001233] rounded-xl transition-all duration-200 overflow-hidden group relative border border-yellow-500/30 hover:border-yellow-400/60 hover:shadow-lg hover:shadow-yellow-500/10"
                    >
                      {/* AGI gradient accent */}
                      <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 pointer-events-none"></div>
                      
                      {/* Card Header */}
                      <div className="p-5 relative z-10 flex flex-col h-full">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-5 h-5 text-yellow-400" />
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                              AGI Event
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(market.status, market.outcome, market)}
                            <button
                              onClick={(e) => handleDelete(e, market.id)}
                              className="p-1.5 rounded-lg transition-all text-gray-500 hover:text-red-500 hover:bg-red-500/10"
                              title="Delete market (Demo only)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        
                        <h3 className="text-base font-bold text-white group-hover:text-yellow-300 transition-colors line-clamp-2 mb-3 min-h-[48px]">
                          {market.question}
                        </h3>
                        
                        {/* AGI Stats - Credit betting */}
                        <div className="mb-3">
                          <div className="flex justify-between text-sm font-bold mb-1.5">
                            <span className="text-emerald-400">YES {odds.yes}%</span>
                            <span className="text-rose-400">NO {odds.no}%</span>
                          </div>
                          <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-full transition-all duration-300 bg-gradient-to-r from-emerald-500 to-rose-500"
                              style={{ width: `${odds.yes}%` }}
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-xs font-medium mt-auto">
                          <div className="flex items-center space-x-1 text-yellow-400">
                            <Coins className="w-3 h-3" />
                            <span>{(market.total_volume || 0).toLocaleString()} credits</span>
                          </div>
                          <div className="flex items-center space-x-1 text-gray-300">
                            <Clock className="w-3 h-3" />
                            <span>{getDaysUntil(market.betting_ends_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hover Footer */}
                      <div className="bg-yellow-500/10 px-5 py-2.5 flex items-center justify-between border-t border-yellow-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <span className="text-xs text-white font-bold uppercase tracking-wider">
                          Bet with credits
                        </span>
                        <ChevronRight className="w-4 h-4 text-yellow-400" />
                      </div>
                    </Link>
                  </motion.div>
                )
              }
              
              // Regular market card
              return (
                <motion.div
                  key={market.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/markets/${market.id}`}
                    className={`block bg-[#001233] rounded-xl transition-all duration-200 overflow-hidden group relative ${
                      isFlash 
                        ? 'border-2 border-red-500/50 hover:border-red-400 shadow-lg shadow-red-500/20 hover:shadow-red-500/40' 
                        : 'border border-[#052393]/40 hover:border-[#2AAAEA]/60 hover:shadow-lg hover:shadow-[#2AAAEA]/10'
                    }`}
                  >
                    {/* Flash market pulsing glow effect */}
                    {isFlash && (
                      <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 animate-pulse pointer-events-none"></div>
                    )}
                    
                    {/* Card Header */}
                    <div className="p-5 relative z-10">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-base font-bold text-white group-hover:text-glow-aqua transition-colors line-clamp-2 flex-1 pr-3">
                          {market.question}
                        </h3>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(market.status, market.outcome, market)}
                          <button
                            onClick={(e) => handleShare(e, market)}
                            className="p-1.5 rounded-lg transition-all text-gray-500 hover:text-cpx-light-blue hover:bg-[#2AAAEA]/10"
                            title="Share market"
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => toggleFavorite(e, market.id)}
                            className={`p-1.5 rounded-lg transition-all ${
                              favorites.includes(market.id)
                                ? 'text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30'
                                : 'text-gray-500 hover:text-cpx-yellow hover:bg-[#FBB03B]/10'
                            }`}
                            title={favorites.includes(market.id) ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <Star className={`w-4 h-4 ${favorites.includes(market.id) ? 'fill-current' : ''}`} />
                          </button>
                          <button
                            onClick={(e) => handleDelete(e, market.id)}
                            className="p-1.5 rounded-lg transition-all text-gray-500 hover:text-red-500 hover:bg-red-500/10"
                            title="Delete market (Demo only)"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Metadata */}
                      <div className="flex items-center space-x-3 text-xs text-gray-400 font-medium mb-4">
                        <span className="capitalize text-white">{market.category}</span>
                        <span>•</span>
                        <span className="flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          {market.creator_name || 'Anon'}
                        </span>
                      </div>
                      
                      {/* Vibrant Progress Bar - Myriad Style */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm font-bold mb-1.5">
                          <span className="text-white">
                            <span className="text-emerald-400 mr-1">↑</span>
                            YES {odds.yes}%
                          </span>
                          <span className="text-white">
                            <span className="text-rose-400 mr-1">↓</span>
                            NO {odds.no}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                          <div 
                            className="h-full transition-all duration-300"
                            style={{
                              width: `${odds.yes}%`,
                              background: 'linear-gradient(90deg, #10b981 0%, #ec4899 100%)'
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center justify-between text-xs font-medium">
                        <div className="flex items-center space-x-1 text-gray-300">
                          <DollarSign className="w-3 h-3" />
                          <span>{(market.total_volume || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-gray-300">
                          <Clock className="w-3 h-3" />
                          <span>{getDaysUntil(market.betting_ends_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hover Footer */}
                    <div className="bg-[#052393]/20 px-5 py-2.5 flex items-center justify-between border-t border-[#052393]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <span className="text-xs text-white font-bold uppercase tracking-wider">View market</span>
                      <ChevronRight className="w-4 h-4 text-cpx-light-blue" />
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
